import { useCallback, useEffect, useMemo, useState } from "react";
import { cloneDeep } from "lodash";
import { Box, Drawer } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";

import CircularProgress from "@mui/material/CircularProgress";
import PageTitle from "../../common/components/PageTitle/PageTitle";
import PageContent from "../../common/components/PageContent";
import SimpleTable, {
	HeadersType,
} from "../../common/components/SimpleTable/SimpleTable";
import {
	useCancelChangeRequestMutation,
	useLazyGetChangeRequestsQuery,
} from "../../requests_cm/gepoChangeRequestService/service";
import { noop } from "../../common/utils/operations";
import { StatusIcon } from "./StatusIcon";
import { FluidButton } from "../../common/components/FluidButton";
import { SearchField } from "../../common/components/SearchField";
import { PrimaryButton } from "../../common/components/CustomButton";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChangeRequestContractCard } from "./ChangeRequestDetailCards/ChangeRequestContractCard";
import {
	ChangeRequestResourceType,
	ChangeRequestStatus,
	ChangeRequestType,
} from "../../requests_cm/gepoChangeRequestService/types";
import { formatDateDDMMYYYY } from "../../common/utils/dateUtils";
import { ActionConfirmationModal } from "../../common/components/ActionConfirmationModal";
import { useIsUserWithinGroups } from "../../common/hooks/useIsUserWithinGroups";
import { Groups } from "../authentication/authentication.constant";
import {
	getPathWithParameters,
	getPathWithSearchParams,
	PATH,
} from "../../router-path";
import { PageInfoType } from "../../requests_cm/gecoTypes";
import { OdataFilter } from "../../requests_cm/gepoTypes";
import { useGetUsersQuery } from "../../requests_cm/gepoUserService/service";
import { enrichUserActiveFilters } from "./changeRequestActiveFilters";
import ChangeRequestFilters from "./ChangeRequestFilters";
import ActiveFilters from "../../common/components/ActiveFilters";
import { transformFilters } from "../../common/components/ActiveFilters/ActiveFilters.utils";
import { Spacer } from "../../common/components/Spacer";
import { If } from "../../common/components/If";
import { ChangeRequestHorizonChangeCard } from "./ChangeRequestDetailCards/ChangeRequestHorizonChangeCard";
import { useRtkQueryDynamicEndpoint } from "../../common/hooks/useRtkQueryDynamicEndpoint";

const style = {
	progressWrapper: {
		display: "flex",
		justifyContent: "center",
	},
	searchWrapper: { display: "flex", alignItems: "center" },
};

const ChangeRequestPage = () => {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();

	const initialFilters = useMemo(() => {
		const filters: Record<string, string[]> = {};
		searchParams.forEach((value, key) => {
			if (!filters[key]) filters[key] = [];
			filters[key].push(value);
		});
		return Object.keys(filters).length > 0 ? filters : undefined;
	}, []);

	const syncFiltersToSearchParams = useCallback(
		(filters: Record<string, unknown>) => {
			const params = new URLSearchParams();
			Object.entries(filters).forEach(([key, value]) => {
				if (Array.isArray(value)) {
					value.forEach((v) => params.append(key, String(v)));
				} else if (value !== undefined && value !== null) {
					params.set(key, String(value));
				}
			});
			setSearchParams(params, { replace: true });
		},
		[setSearchParams]
	);

	const [isFilterOpen, setFilterOpen] = useState(false);

	const [pageInfo, setPageInfo] = useState<PageInfoType>({
		page: 1,
		search: "",
		filters: initialFilters || {},
	});

	const [selectedChangeRequest, setChangeRequest] = useState<
		ChangeRequestType | undefined
	>();
	const [selectedRequestToCancel, setSelectedRequestToCancel] = useState<
		ChangeRequestType | undefined
	>();

	const mapFilterFieldToOdata = (
		filters: Record<string, string[]>
	): OdataFilter[] => {
		const odataList: OdataFilter[] = [];
		for (const key of Object.keys(filters)) {
			const filterValue = filters[key];
			const keyParts = key.split("__");
			const fieldPath = keyParts.at(-2) as string;
			const operation = keyParts.at(-1) as string;
			odataList.push({
				value: filterValue,
				field_path: fieldPath,
				operation,
			});
		}
		return odataList;
	};

	const [
		baseGetChangeRequests,
		{ data: changeRequestsResult, isLoading, isFetching },
	] = useLazyGetChangeRequestsQuery();

	const getChangeRequests = useRtkQueryDynamicEndpoint(baseGetChangeRequests);

	useEffect(() => {
		getChangeRequests({
			...pageInfo,
			filters: mapFilterFieldToOdata(pageInfo?.filters || {}),
		});
	}, [pageInfo]);

	const [cancelRequest, { isLoading: isCancelLoading }] =
		useCancelChangeRequestMutation();

	const { isUserAuthorized } = useIsUserWithinGroups();

	const navigateToChangeRequestReview = useCallback(
		(changeRequestId: number) => {
			const path = getPathWithParameters(
				PATH.CHANGE_REQUESTS_VALIDATION,
				{
					changeRequestId,
				}
			);
			navigate(path, { state: { filters: searchParams.toString() } });
		},
		[searchParams]
	);

	const isUserTrader = useMemo(() => {
		return isUserAuthorized([Groups.trader]);
	}, [isUserAuthorized]);

	const onEditChangeRequest = (editableChangeRequest: ChangeRequestType) => {
		const editPageUri = getPathWithSearchParams(
			editableChangeRequest.resource_edit_path,
			{ changeRequestId: editableChangeRequest.id }
		);
		navigate(editPageUri, { state: { filters: searchParams.toString() } });
	};

	const handleSearch = useCallback((searchValue: string) => {
		setPageInfo((current) => ({
			...current,
			page: 1,
			search: searchValue,
		}));
	}, []);

	const { data: users = [] } = useGetUsersQuery();
	const userNameById = useMemo(
		() =>
			users.reduce<Record<string, string>>((acc, user) => {
				acc[String(user.id)] = user.name;
				return acc;
			}, {}),
		[users]
	);

	const activeFilters = useMemo(() => {
		const filtersByKeys = pageInfo?.filters
			? transformFilters(pageInfo?.filters)
			: {};
		return enrichUserActiveFilters(filtersByKeys, userNameById);
	}, [pageInfo, userNameById]);

	const isLastPage = useMemo(() => {
		return !changeRequestsResult?.has_next_page;
	}, [changeRequestsResult]);

	const items = (changeRequestsResult?.data || []).map((changeRequest) => {
		return {
			...changeRequest,
			status: (
				<StatusIcon
					status={changeRequest?.status}
					validatorMessage={changeRequest?.validator_message}
				/>
			),
			requester: (
				<p style={{ textTransform: "capitalize" }}>
					{changeRequest?.requester?.first_name}{" "}
					{changeRequest?.requester?.last_name}
				</p>
			),
			validator: (
				<p style={{ textTransform: "capitalize" }}>
					{changeRequest?.validator?.first_name}{" "}
					{changeRequest?.validator?.last_name}
				</p>
			),
			action: isUserTrader ? (
				changeRequest?.status === ChangeRequestStatus.PENDING ? (
					<FluidButton
						variant="secondary"
						onClick={() =>
							navigateToChangeRequestReview(changeRequest?.id)
						}
						label={"Review request"}
					/>
				) : (
					<FluidButton
						variant="secondary"
						onClick={() => setChangeRequest(changeRequest)}
						label={"View details"}
					/>
				)
			) : (
				<FluidButton
					variant="secondary"
					onClick={() => setChangeRequest(changeRequest)}
					label={"View details"}
				/>
			),
			created_at: <p>{formatDateDDMMYYYY(changeRequest?.created_at)}</p>,
		};
	});

	const removeSingleActiveFilter = useCallback(
		({
			filter_key,
			operation,
		}: {
			filter_key: string;
			operation: string;
		}) => {
			const pageInfoCopy = cloneDeep<PageInfoType>(pageInfo);
			if (pageInfoCopy?.filters) {
				delete pageInfoCopy.filters[`${filter_key}__${operation}`];
				pageInfoCopy.page = 1;
				setPageInfo(pageInfoCopy);
				syncFiltersToSearchParams(pageInfoCopy.filters);
			}
		},
		[pageInfo, syncFiltersToSearchParams]
	);

	const removeAllFilters = useCallback(() => {
		if (pageInfo?.filters) {
			const pageInfoCopy = cloneDeep<PageInfoType>(pageInfo);
			pageInfoCopy.filters = {};
			pageInfoCopy.page = 1;
			setPageInfo(pageInfoCopy);
			syncFiltersToSearchParams({});
		}
	}, [pageInfo, syncFiltersToSearchParams]);

	const headers: HeadersType<ChangeRequestType>[] = [
		{ label: "Request Type", accessor: "resource_type" },
		{ label: "Contract name", accessor: "name" },
		{ label: "Requester", accessor: "requester" },
		{ label: "Validator", accessor: "validator" },
		{ label: "Request date", accessor: "created_at" },
		{ label: "Status", accessor: "status" },
		{ label: "Actions", accessor: "action" },
	].map((header) => ({
		...header,
	})) as HeadersType<ChangeRequestType>[];

	return (
		<>
			<PageTitle
				fontWeight={"bold"}
				label="Change Requests"
				leftSide={
					<PrimaryButton
						text="Back"
						type="button"
						color="info"
						sx={{
							width: 106,
							color: "#171D21",
							marginRight: "16px",
						}}
						onClick={() => navigate(PATH.CONTRACTS)}
					>
						<ChevronLeftIcon />
					</PrimaryButton>
				}
			/>

			{isLoading && (
				<Box sx={style.progressWrapper}>
					<CircularProgress data-testid="loader" />
				</Box>
			)}
			{!isLoading && !isCancelLoading && items && (
				<PageContent>
					<Box sx={style.searchWrapper}>
						<SearchField
							defaultValue={pageInfo.search}
							onSearch={handleSearch}
						/>
						<FluidButton
							label={"Filters"}
							icon="filter_list"
							onClick={() => setFilterOpen(true)}
							variant="secondary"
						/>
					</Box>
					<Spacer gap={32} />
					<ActiveFilters
						filters={activeFilters}
						removeFilter={removeSingleActiveFilter}
						resetFilters={removeAllFilters}
					/>
					<Spacer gap={32} />

					<SimpleTable
						handleRowClick={noop}
						headers={headers}
						infiniteScroll
						isFetching={isFetching}
						isLastPage={isLastPage}
						items={items}
						pageInfo={pageInfo}
						setPageInfo={setPageInfo}
					/>
				</PageContent>
			)}
			<Drawer
				PaperProps={{
					sx: { width: "800px" },
				}}
				anchor={"right"}
				open={!!selectedChangeRequest}
				onClose={() => setChangeRequest(undefined)}
			>
				<If
					condition={[
						ChangeRequestResourceType.HORIZON_CHANGE,
					].includes(
						selectedChangeRequest?.resource_type as ChangeRequestResourceType
					)}
				>
					<ChangeRequestHorizonChangeCard
						onEdit={() =>
							onEditChangeRequest(
								selectedChangeRequest as ChangeRequestType
							)
						}
						onCancel={() =>
							setSelectedRequestToCancel(selectedChangeRequest)
						}
						changeRequest={selectedChangeRequest}
						onClose={() => setChangeRequest(undefined)}
						hideActions={isUserTrader}
					/>
				</If>
				<If
					condition={[
						ChangeRequestResourceType.CONTRACT,
						ChangeRequestResourceType.CONTRACT_PERIOD,
					].includes(
						selectedChangeRequest?.resource_type as ChangeRequestResourceType
					)}
				>
					<ChangeRequestContractCard
						onEdit={() =>
							onEditChangeRequest(
								selectedChangeRequest as ChangeRequestType
							)
						}
						onCancel={() =>
							setSelectedRequestToCancel(selectedChangeRequest)
						}
						changeRequest={selectedChangeRequest}
						onClose={() => setChangeRequest(undefined)}
						hideActions={isUserTrader}
					/>
				</If>
			</Drawer>
			<ActionConfirmationModal
				isOpen={!!selectedRequestToCancel}
				title="Close request confirmation"
				text="Are you sure you want to close this request?"
				onClose={() => setSelectedRequestToCancel(undefined)}
				onConfirm={() => {
					cancelRequest({
						id: selectedRequestToCancel?.id as number,
					});
					setSelectedRequestToCancel(undefined);
					setChangeRequest(undefined);
				}}
			/>
			<ChangeRequestFilters
				isOpen={isFilterOpen}
				defaultFilters={pageInfo?.filters}
				onClose={() => setFilterOpen(false)}
				onApplyFilters={(filters) => {
					setFilterOpen(false);
					setPageInfo((current) => ({
						page: 1,
						search: current.search,
						filters,
					}));
					syncFiltersToSearchParams(filters);
				}}
			/>
		</>
	);
};

export default ChangeRequestPage;
